import { Box, Flex, Text, UnorderedList, ListItem } from '@chakra-ui/react';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  getCompareDashboardScenario,
  getDashboard,
  getSelectedDashboardScenario,
  getTimeFilter,
} from 'redux/DashboardSliceV2';
import { getBlocks } from 'redux/ModelsSlice';
import {
  formatMonth,
  getCurrentMonthYyyyMm,
  REPORTING_WINDOW_LABELS,
  resolveReportingPeriod,
  type ReportingWindow,
} from 'utils/reportingPeriodUtils';
import { font, fontSize, neutral, radius, text } from 'styles/revampTokens';

type DashboardPage = { id?: number | string; name?: string; position?: number };

/**
 * Read-only summary of what the server pack export will include (dashboard, pages, scenario, period).
 */
const ExportPackSummary = () => {
  const dashboard = useSelector(getDashboard);
  const model = useSelector(getBlocks);
  const selectedScenario = useSelector(getSelectedDashboardScenario);
  const compareScenario = useSelector(getCompareDashboardScenario);
  const timeFilter = useSelector(getTimeFilter);
  const anchorPeriod = useSelector(
    (state: { dashboardSliceV2?: { reporting_anchor_period?: string } }) =>
      state.dashboardSliceV2?.reporting_anchor_period || getCurrentMonthYyyyMm(),
  );
  const reportingWindow = useSelector(
    (state: { dashboardSliceV2?: { reporting_window?: ReportingWindow } }) =>
      state.dashboardSliceV2?.reporting_window || 'SINGLE',
  );

  const pages = useMemo(() => {
    const rows = (dashboard?.pages ?? []) as DashboardPage[];
    return [...rows].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  }, [dashboard?.pages]);

  const periodLabel = useMemo(() => {
    const fyStart = (model?.time_properties?.fy_start_month as string) || 'Jan';
    if (reportingWindow === 'DISPLAY_TIMESCALE') {
      const start = timeFilter?.start;
      const end = timeFilter?.end;
      if (start && end) return `Display timescale · ${start} – ${end}`;
      return 'Display timescale';
    }
    const windowLabel = REPORTING_WINDOW_LABELS[reportingWindow] ?? reportingWindow;
    const resolved = resolveReportingPeriod(anchorPeriod, reportingWindow, fyStart);
    const anchorMatch = /^(\d{4})-(\d{2})$/.exec(anchorPeriod);
    const anchorDisplay = anchorMatch
      ? formatMonth(new Date(Number(anchorMatch[1]), Number(anchorMatch[2]) - 1, 1))
      : anchorPeriod;
    if (resolved.start && resolved.end && resolved.start !== resolved.end) {
      return `${windowLabel} · anchor ${anchorDisplay} (${resolved.start} – ${resolved.end})`;
    }
    return `${windowLabel} · anchor ${anchorDisplay}`;
  }, [
    anchorPeriod,
    model?.time_properties?.fy_start_month,
    reportingWindow,
    timeFilter?.end,
    timeFilter?.start,
  ]);

  const scenarioLine = useMemo(() => {
    const primary = selectedScenario?.name ?? 'Default scenario';
    if (compareScenario?.name) {
      return `${primary} vs ${compareScenario.name}`;
    }
    return primary;
  }, [compareScenario?.name, selectedScenario?.name]);

  const dashboardTitle = dashboard?.name ?? 'Dashboard';

  return (
    <Box
      border={`1px solid ${neutral.border}`}
      borderRadius={radius.control}
      bg={neutral.surfaceMuted}
      p={4}
    >
      <Text
        fontFamily={font.display}
        fontSize={fontSize.body}
        fontWeight={600}
        color={text.primary}
      >
        {dashboardTitle}
      </Text>
      <Flex direction='column' gap={2} mt={3} fontSize={fontSize.label} color={text.secondary}>
        <Flex gap={2} align='baseline'>
          <Text fontWeight={600} color={text.primary} minW='72px' flexShrink={0}>
            Scenario
          </Text>
          <Text>{scenarioLine}</Text>
        </Flex>
        <Flex gap={2} align='baseline'>
          <Text fontWeight={600} color={text.primary} minW='72px' flexShrink={0}>
            Period
          </Text>
          <Text>{periodLabel}</Text>
        </Flex>
        <Box>
          <Text fontWeight={600} color={text.primary} mb={1}>
            Pages ({pages.length})
          </Text>
          {pages.length === 0 ? (
            <Text color={text.muted}>No pages on this dashboard.</Text>
          ) : (
            <UnorderedList m={0} pl={5} spacing={0.5}>
              {pages.map((page, index) => (
                <ListItem key={String(page.id ?? index)} color={text.secondary}>
                  {page.name || `Page ${index + 1}`}
                </ListItem>
              ))}
            </UnorderedList>
          )}
        </Box>
      </Flex>
    </Box>
  );
};

export default ExportPackSummary;
